import React, { useEffect, useState } from 'react';
import { 
  Copy, Check, ArrowLeft, Save, FileText, Settings, 
  HelpCircle, Hash, Sparkles, AlertCircle
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import FormQuestionBuilder from '../components/FormQuestionBuilder';
import apiService from '../services/api';
import { Form as FormType, Question } from '../types/api';

const FormBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [formName, setFormName] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState<boolean>(!!id);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const form = await apiService.getFormById(id);
        setFormName(form.form_name);
        setSubTitle(form.sub_title || '');
        setIsOnboarding(form.is_OnBoarding);
        const normalized: Question[] = (form.questions || []).map((q) => {
          const mcq = q.answers_mcq;
          const options = mcq?.options || ['Yes', 'No'];
          const interpretations = mcq?.interpretations || [];
          const normalizedInterpretations = options.map((_, idx) => interpretations[idx] || '');
          const scores = mcq?.score || [];
          const normalizedScores = options.map((_, idx) => scores[idx] ?? 0);
          
          return {
            ...q,
            isRequired: typeof q.isRequired === 'string' ? q.isRequired === 'true' : !!q.isRequired,
            answer_text: q.answer_text ?? '',
            answers_mcq: (q.answer_type === 'mcq' || q.answer_type === 'checkbox') 
              ? { options, interpretations: normalizedInterpretations, score: normalizedScores, answer: mcq?.answer || '' }
              : undefined,
          };
        });
        setQuestions(normalized);
      } catch (e: any) {
        setError(e.message || 'Failed to load form');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Auto-clear messages
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleCopyId = async () => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Failed to copy form id', e);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formName.trim()) {
      setError('Form name is required');
      return;
    }

    try {
      setSaving(true);
      const normalizedQuestions: Question[] = (questions || []).map((q) => {
        const mcq = q.answers_mcq;
        const options = mcq?.options || ['Yes', 'No'];
        const interpretations = mcq?.interpretations || [];
        const normalizedInterpretations = options.map((_, idx) => interpretations[idx] || '');
        const scores = mcq?.score || [];
        const normalizedScores = options.map((_, idx) => scores[idx] ?? 0);
        
        return {
          ...q,
          isRequired: !!q.isRequired,
          answer_text: q.answer_text ?? '',
          answers_mcq: (q.answer_type === 'mcq' || q.answer_type === 'checkbox') 
            ? { options, interpretations: normalizedInterpretations, score: normalizedScores, answer: mcq?.answer || '' }
            : undefined,
        };
      });

      const payload: FormType = {
        form_name: formName,
        sub_title: subTitle || undefined,
        is_OnBoarding: isOnboarding,
        questions: normalizedQuestions,
      };

      if (id) {
        await apiService.updateForm(id, payload);
        setSuccess('Form updated successfully!');
      } else {
        await apiService.createForm(payload);
        setSuccess('Form created successfully!');
      }
      
      setTimeout(() => navigate('/forms'), 1500);
    } catch (e: any) {
      setError(e.message || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const getFormStats = () => {
    const total = questions.length;
    const required = questions.filter(q => q.isRequired).length;
    const mcq = questions.filter(q => q.answer_type === 'mcq' || q.answer_type === 'checkbox').length;
    const text = questions.filter(q => q.answer_type === 'text' || q.answer_type === 'number').length;
    return { total, required, mcq, text };
  };

  const stats = getFormStats();

  if (loading) {
    return (
      <div className="form-builder-page">
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-builder-page">
      {/* Header */}
      <div className="form-builder-header">
        <div className="form-builder-header-left">
          <button onClick={() => navigate('/forms')} className="back-button">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="form-builder-title-row">
              <h2 className="form-builder-title">
                {id ? 'Edit Form' : 'Create New Form'}
              </h2>
              {id && (
                <button
                  type="button"
                  className="copy-id-btn"
                  title="Copy Form ID"
                  onClick={handleCopyId}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copied!' : 'Copy ID'}
                </button>
              )}
            </div>
            <p className="form-builder-subtitle">
              {id ? `Editing form: ${formName || 'Untitled'}` : 'Design your form with questions and settings'}
            </p>
          </div>
        </div>
        <div className="form-builder-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/forms')}>
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="spinner-small"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                {id ? 'Save Changes' : 'Create Form'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="message message-error">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {success && (
        <div className="message message-success">
          <Check size={18} />
          {success}
        </div>
      )}

      {/* Stats Bar */}
      {questions.length > 0 && (
        <div className="form-stats-bar">
          <div className="form-stat">
            <HelpCircle size={16} />
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Questions</span>
          </div>
          <div className="form-stat">
            <Sparkles size={16} />
            <span className="stat-value">{stats.required}</span>
            <span className="stat-label">Required</span>
          </div>
          <div className="form-stat">
            <FileText size={16} />
            <span className="stat-value">{stats.mcq}</span>
            <span className="stat-label">MCQ/Checkbox</span>
          </div>
          <div className="form-stat">
            <Hash size={16} />
            <span className="stat-value">{stats.text}</span>
            <span className="stat-label">Text/Number</span>
          </div>
        </div>
      )}

      <div className="form-builder-content">
        {/* Form Details Section */}
        <div className="form-builder-section">
          <div className="section-header">
            <div className="section-icon">
              <Settings size={20} />
            </div>
            <div>
              <h3 className="section-title">Form Settings</h3>
              <p className="section-subtitle">Configure your form's basic information</p>
            </div>
          </div>
          <div className="section-body">
            <div className="form-settings-grid">
              <div className="form-field-group">
                <label className="form-field-label">
                  Form Name <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  className="form-field-input"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Enter a descriptive form name..."
                />
              </div>
              <div className="form-field-group">
                <label className="form-field-label">Sub Title</label>
                <input
                  type="text"
                  className="form-field-input"
                  value={subTitle}
                  onChange={(e) => setSubTitle(e.target.value)}
                  placeholder="Optional sub-title for the form..."
                />
              </div>
              <div className="form-field-group">
                <label className="form-field-label">Form Type</label>
                <div className="form-type-toggle">
                  <button
                    type="button"
                    className={`type-option ${!isOnboarding ? 'active' : ''}`}
                    onClick={() => setIsOnboarding(false)}
                  >
                    <FileText size={16} />
                    Regular Form
                  </button>
                  <button
                    type="button"
                    className={`type-option ${isOnboarding ? 'active' : ''}`}
                    onClick={() => setIsOnboarding(true)}
                  >
                    <Sparkles size={16} />
                    Onboarding Form
                  </button>
                </div>
              </div>
            </div>

            {id && (
              <div className="form-meta-info">
                <div className="meta-item">
                  <Hash size={14} />
                  <span className="meta-label">Form ID:</span>
                  <code className="meta-value">{id}</code>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Questions Section */}
        <div className="form-builder-section">
          <div className="section-header">
            <div className="section-icon questions-icon">
              <HelpCircle size={20} />
            </div>
            <div>
              <h3 className="section-title">Questions</h3>
              <p className="section-subtitle">Add and configure questions for your form</p>
            </div>
          </div>
          <div className="section-body">
            <FormQuestionBuilder value={questions} onChange={setQuestions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
